/**
 * Tab + carousel behaviour for the "Self-Care Shelf" collection tab sections:
 *   - sections/custom-carousal.liquid            (data-ac-collection-tabs="home":
 *       main tabs + desktop sub-collection sidebar, sub tabs fetched on demand)
 *   - sections/custom-carousal-collection.liquid (data-ac-collection-tabs="collection":
 *       main tabs only, scrollable tab strip)
 *
 * This used to be an inline <script> per section instance (22KB of it on the
 * homepage). It is now one deferred, cacheable file. Behaviour is unchanged
 * except for one addition: every tab panel other than the initially active
 * one ships its markup inside <template data-tab-template>, and
 * materializeTabPanel() moves that content into the panel the first time its
 * tab is clicked - before the .active class (and its fade-in animation) is
 * applied, so the switch looks exactly as it did when every panel was in the
 * DOM from the start. Images keep their loading="lazy" when materialised.
 */
(function () {
  if (window.acCollectionTabsInit) return;

  function initializeCollectionTabs(sectionElement) {
    if (!sectionElement || sectionElement.dataset.collectionTabsInitialized === 'true') {
      return;
    }

    sectionElement.dataset.collectionTabsInitialized = 'true';

    var variant = sectionElement.getAttribute('data-ac-collection-tabs') || 'home';
    var scrollsTabStrip = variant === 'collection';
    var rootUrl = sectionElement.getAttribute('data-root-url') || '/';
    var viewAllButton = sectionElement.querySelector('.view-all-btn');
    var desktopSubTabsBound = false;
    var resizeFrame = null;
    var sidebarHeightFrame = null;
    var pendingSidebarContent = null;
    // Caches in-flight/completed fetches per collection handle so re-opening
    // the same tab (or two blocks sharing a sub-collection) never re-fetches.
    var subCollectionFetchCache = {};
    // Tracks each .sub-content element's hydration promise so the click
    // handler can wait for real content before switching the visible tab
    // (and whatever CSS reveal animation is tied to .active), instead of
    // switching immediately and animating in an empty container.
    var hydrationPromises = new WeakMap();

    function isDesktop() {
      return window.innerWidth > 768;
    }

    function getActiveMainTab() {
      return sectionElement.querySelector('.main-tab-btn.active');
    }

    function setViewAllUrl(tabButton) {
      if (!viewAllButton || !tabButton) return;

      var url = tabButton.getAttribute('data-url');

      if (url) {
        viewAllButton.setAttribute('href', url);
      } else {
        viewAllButton.removeAttribute('href');
      }
    }

    /*
      Inactive tab panels are server-rendered inside an inert <template> so
      their product cards (forms, images, SVGs) stay out of the live DOM until
      the tab is actually opened. Moving template.content into the panel keeps
      the exact server markup; custom elements upgrade on insertion.
    */
    function materializeTabPanel(panel) {
      if (!panel) return;

      var template = null;

      for (var i = 0; i < panel.children.length; i++) {
        if (panel.children[i].tagName === 'TEMPLATE' && panel.children[i].hasAttribute('data-tab-template')) {
          template = panel.children[i];
          break;
        }
      }

      if (!template) return;

      panel.replaceChild(template.content, template);
    }

    /*
      Brings the active tab fully inside the scrollable tab strip. The wrapper
      is resolved from the button itself so this stays correct even if the
      section is duplicated. offsetLeft is measured against the wrapper (its
      offsetParent) and, unlike getBoundingClientRect, does not shift as the
      strip scrolls.
    */
    function scrollTabIntoView(tabButton, smooth) {
      if (!scrollsTabStrip || !tabButton) return;

      var wrapper = tabButton.closest('.main-tabs-wrapper');

      if (!wrapper) return;

      window.requestAnimationFrame(function () {
        var maximumScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;

        if (maximumScrollLeft <= 0) return;

        var peekAmount = 64; // ~32px of the next tab stays visible after the 16px gap
        var tabLeft = tabButton.offsetLeft;
        var tabRight = tabLeft + tabButton.offsetWidth;
        var viewLeft = wrapper.scrollLeft;
        var viewRight = viewLeft + wrapper.clientWidth;
        var target = viewLeft;

        if (tabRight > viewRight - peekAmount) {
          target = tabRight - wrapper.clientWidth + peekAmount;
        } else if (tabLeft < viewLeft + peekAmount) {
          target = tabLeft - peekAmount;
        }

        target = Math.max(0, Math.min(maximumScrollLeft, target));

        if (Math.abs(target - viewLeft) < 1) return;

        wrapper.scrollTo({
          left: target,
          behavior: smooth === false ? 'auto' : 'smooth',
        });
      });
    }

    function getVisibleWrappers(scope) {
      var wrappers = (scope || sectionElement).querySelectorAll('.collection-products-scroller');

      return Array.prototype.filter.call(wrappers, function (wrapper) {
        var mainContent = wrapper.closest('.collection-tab-content');
        var subContent = wrapper.closest('.sub-content');

        var mainVisible = !mainContent || mainContent.classList.contains('active');
        var subVisible = !subContent || subContent.classList.contains('active');

        return mainVisible && subVisible;
      });
    }

    function getProductCards(scroller) {
      return Array.prototype.filter.call(scroller.children, function (child) {
        return child.nodeType === 1 && child.offsetParent !== null;
      });
    }

    function getActualProductRowHeight(mainContent) {
      if (!mainContent) return 0;

      var activeSubContent = mainContent.querySelector('.sub-content.active');
      var productScrollerWrapper = activeSubContent
        ? activeSubContent.querySelector('.collection-products-scroller')
        : null;
      var scroller = productScrollerWrapper
        ? productScrollerWrapper.querySelector('.Category_innersec_custom')
        : null;
      var cards = scroller ? getProductCards(scroller) : [];

      if (!productScrollerWrapper || !scroller || !cards.length) {
        return 0;
      }

      var scrollerRect = scroller.getBoundingClientRect();
      var maximumBottom = scrollerRect.top;

      cards.forEach(function (card) {
        var cardRect = card.getBoundingClientRect();

        maximumBottom = Math.max(maximumBottom, cardRect.bottom, cardRect.top + card.scrollHeight);

        /*
          Some product-grid snippets use nested wrappers or absolutely
          positioned card sections. Include their real visual bottom so the
          sidebar follows the complete product card rather than only the
          outer grid item's reported height.
        */
        var measurableElements = card.querySelectorAll(
          '.grid-product__content, ' +
            '.grid-product__wrapper, ' +
            '.grid-product__link, ' +
            '.grid-product__meta, ' +
            '.product-card, ' +
            '.product-card__inner, ' +
            '.product-card__content, ' +
            '.product-card__information, ' +
            '.Addtocart_buttonsec'
        );

        measurableElements.forEach(function (element) {
          if (element.offsetParent === null) return;

          var elementRect = element.getBoundingClientRect();

          maximumBottom = Math.max(maximumBottom, elementRect.bottom, elementRect.top + element.scrollHeight);
        });
      });

      var renderedContentHeight = Math.ceil(maximumBottom - scrollerRect.top);
      var scrollerBoxHeight = Math.ceil(scroller.getBoundingClientRect().height);
      var scrollerScrollHeight = Math.ceil(scroller.scrollHeight);
      var wrapperHeight = Math.ceil(productScrollerWrapper.getBoundingClientRect().height);

      return Math.max(renderedContentHeight, scrollerBoxHeight, scrollerScrollHeight, wrapperHeight);
    }

    function syncSidebarHeight(mainContent) {
      if (!mainContent) return;

      var sidebarWrapper = mainContent.querySelector('.sub-tab-sidebar-wrapper');

      if (!sidebarWrapper) return;

      if (!isDesktop()) {
        sidebarWrapper.style.removeProperty('--sub-tab-sidebar-height');
        sidebarWrapper.removeAttribute('data-measured-sidebar-height');
        return;
      }

      var productRowHeight = getActualProductRowHeight(mainContent);

      if (productRowHeight <= 0) {
        return;
      }

      var previousHeight = parseInt(sidebarWrapper.getAttribute('data-measured-sidebar-height') || '0', 10);

      /*
        Avoid unnecessary style writes and ResizeObserver feedback loops.
        The 550px CSS fallback is already visible before this measurement runs.
      */
      if (Math.abs(previousHeight - productRowHeight) <= 1) {
        return;
      }

      sidebarWrapper.setAttribute('data-measured-sidebar-height', String(productRowHeight));

      sidebarWrapper.style.setProperty('--sub-tab-sidebar-height', productRowHeight + 'px');
    }

    function scheduleSidebarHeightSync(mainContent) {
      if (!mainContent || !isDesktop()) return;

      pendingSidebarContent = mainContent;

      if (sidebarHeightFrame) {
        return;
      }

      sidebarHeightFrame = window.requestAnimationFrame(function () {
        sidebarHeightFrame = null;

        var contentToMeasure = pendingSidebarContent;
        pendingSidebarContent = null;

        syncSidebarHeight(contentToMeasure);
      });
    }

    function updateArrowPosition(wrapper) {
      var scroller = wrapper.querySelector('.Category_innersec_custom');
      var cards = scroller ? getProductCards(scroller) : [];

      if (!scroller || !cards.length) return;

      var wrapperRect = wrapper.getBoundingClientRect();
      var scrollerRect = scroller.getBoundingClientRect();
      var firstCardRect = cards[0].getBoundingClientRect();

      var previousLeft = scrollerRect.left - wrapperRect.left;
      var nextLeft = scrollerRect.right - wrapperRect.left;
      var arrowTop = firstCardRect.top - wrapperRect.top + firstCardRect.height / 2;

      // Custom properties inherit, so rewriting them (even unchanged) would
      // invalidate style for every card in the row and force a re-layout on
      // the next read. Init runs this several times (immediately, on load, on
      // fonts.ready, after 100ms), so skip the write when nothing moved.
      setVar(wrapper, '--collection-arrow-prev-left', previousLeft + 'px');
      setVar(wrapper, '--collection-arrow-next-left', nextLeft + 'px');
      setVar(wrapper, '--collection-arrow-top', arrowTop + 'px');
    }

    function setVar(element, name, value) {
      if (element.style.getPropertyValue(name) === value) return;
      element.style.setProperty(name, value);
    }

    function updateScrollerButtons(wrapper) {
      var scroller = wrapper.querySelector('.Category_innersec_custom');
      var previousButton = wrapper.querySelector('.collection-carousel-chevron--prev');
      var nextButton = wrapper.querySelector('.collection-carousel-chevron--next');

      if (!scroller || !previousButton || !nextButton) return;

      updateArrowPosition(wrapper);

      if (!isDesktop()) {
        previousButton.disabled = true;
        nextButton.disabled = true;
        previousButton.style.display = 'none';
        nextButton.style.display = 'none';
        return;
      }

      var maximumScrollLeft = Math.max(0, Math.round(scroller.scrollWidth - scroller.clientWidth));

      if (maximumScrollLeft <= 5) {
        previousButton.disabled = true;
        nextButton.disabled = true;
        previousButton.style.display = 'none';
        nextButton.style.display = 'none';
        return;
      }

      previousButton.style.display = '';
      nextButton.style.display = '';

      var currentScrollLeft = Math.round(scroller.scrollLeft);
      var threshold = 5;

      previousButton.disabled = currentScrollLeft <= threshold;
      nextButton.disabled = currentScrollLeft >= maximumScrollLeft - threshold;
    }

    function getScrollDistance(scroller) {
      var cards = getProductCards(scroller);

      if (!cards.length) {
        return scroller.clientWidth * 0.8;
      }

      if (cards[1]) {
        return Math.round(cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left);
      }

      var scrollerStyles = window.getComputedStyle(scroller);
      var gap = parseFloat(scrollerStyles.columnGap || scrollerStyles.gap) || 0;

      return cards[0].getBoundingClientRect().width + gap;
    }

    function scrollOneCard(scroller, direction) {
      var distance = getScrollDistance(scroller);
      var currentScrollLeft = scroller.scrollLeft;
      var targetIndex;

      if (direction > 0) {
        targetIndex = Math.ceil((currentScrollLeft + 1) / distance);
      } else {
        targetIndex = Math.floor((currentScrollLeft - 1) / distance);
      }

      scroller.scrollTo({
        left: Math.max(0, targetIndex * distance),
        behavior: 'smooth',
      });
    }

    function initializeScroller(wrapper) {
      if (!wrapper || wrapper.dataset.collectionScrollerInitialized === 'true') {
        return;
      }

      var scroller = wrapper.querySelector('.Category_innersec_custom');
      var previousButton = wrapper.querySelector('.collection-carousel-chevron--prev');
      var nextButton = wrapper.querySelector('.collection-carousel-chevron--next');

      if (!scroller || !previousButton || !nextButton) return;

      wrapper.dataset.collectionScrollerInitialized = 'true';

      previousButton.addEventListener('click', function () {
        if (!isDesktop()) return;
        scrollOneCard(scroller, -1);
      });

      nextButton.addEventListener('click', function () {
        if (!isDesktop()) return;
        scrollOneCard(scroller, 1);
      });

      scroller.addEventListener(
        'scroll',
        function () {
          if (!isDesktop()) return;
          updateScrollerButtons(wrapper);
        },
        { passive: true }
      );

      var mainContent = wrapper.closest('.collection-tab-content');

      if ('ResizeObserver' in window) {
        var scrollerResizeObserver = new ResizeObserver(function () {
          updateScrollerButtons(wrapper);
          scheduleSidebarHeightSync(mainContent);
        });

        /*
          Observing the product row is sufficient because its rendered height
          changes when card content, fonts, or images change. Observing every
          card separately caused repeated measurement callbacks.
        */
        scrollerResizeObserver.observe(scroller);
        wrapper.collectionScrollerResizeObserver = scrollerResizeObserver;
      }

      var pendingImages = Array.prototype.filter.call(scroller.querySelectorAll('img'), function (image) {
        return !image.complete;
      });

      if (pendingImages.length) {
        var remainingImages = pendingImages.length;

        var handleImageComplete = function () {
          remainingImages -= 1;

          if (remainingImages > 0) return;

          updateScrollerButtons(wrapper);
          scheduleSidebarHeightSync(mainContent);
        };

        pendingImages.forEach(function (image) {
          image.addEventListener('load', handleImageComplete, { once: true });
          image.addEventListener('error', handleImageComplete, { once: true });
        });
      }

      updateScrollerButtons(wrapper);
      scheduleSidebarHeightSync(mainContent);
    }

    function initializeVisibleScrollers(scope) {
      if (!isDesktop()) return;

      getVisibleWrappers(scope).forEach(function (wrapper) {
        initializeScroller(wrapper);
        updateScrollerButtons(wrapper);
        scheduleSidebarHeightSync(wrapper.closest('.collection-tab-content'));
      });
    }

    function resetVisibleScrollerPositions(scope) {
      getVisibleWrappers(scope).forEach(function (wrapper) {
        var scroller = wrapper.querySelector('.Category_innersec_custom');

        if (scroller) {
          scroller.scrollLeft = 0;
        }
      });
    }

    function parseProductGridItems(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      // The response is sections/custom-carousal.liquid rendered through the
      // Section Rendering API on the sub-collection's page, which outputs
      // only the product cards (see the "card feed" branch at the top of
      // that file).
      var scope = doc.querySelector('.collection-tabs-card-feed') || doc;
      var cards = scope.querySelectorAll('.ac-snippet-card');

      return Array.prototype.slice.call(cards, 0, 8);
    }

    function fetchSubCollectionProducts(handle) {
      if (!handle) {
        return Promise.resolve([]);
      }

      if (subCollectionFetchCache[handle]) {
        return subCollectionFetchCache[handle];
      }

      // Render the custom-carousal section file (by name) on the
      // sub-collection's own page via the Section Rendering API. With no
      // blocks it outputs just that collection's product cards, so the
      // response stays small.
      var params = new URLSearchParams();
      params.set('section_id', 'custom-carousal');
      var previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewThemeId) {
        params.set('preview_theme_id', previewThemeId);
      }

      var query = params.toString();
      var url = rootUrl + 'collections/' + encodeURIComponent(handle) + (query ? '?' + query : '');

      var request = fetch(url)
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Sub-collection fetch failed: ' + response.status);
          }
          return response.text().then(function (html) {
            return parseProductGridItems(html);
          });
        })
        .catch(function () {
          // Allow a later click to retry instead of caching a failure.
          delete subCollectionFetchCache[handle];
          return [];
        });

      subCollectionFetchCache[handle] = request;

      return request;
    }

    function hydrateSubContent(subContent) {
      if (!subContent || !isDesktop()) {
        return Promise.resolve();
      }

      if (subContent.dataset.hydrated === 'true') {
        return Promise.resolve();
      }

      if (hydrationPromises.has(subContent)) {
        return hydrationPromises.get(subContent);
      }

      var handle = subContent.getAttribute('data-collection-handle');
      var grid = subContent.querySelector('.Category_innersec_custom');

      if (!handle || !grid) {
        return Promise.resolve();
      }

      var promise = fetchSubCollectionProducts(handle).then(function (items) {
        // The tab may have been re-rendered/removed while the fetch was in flight.
        if (!subContent.isConnected) return;

        items.forEach(function (item) {
          grid.appendChild(item.cloneNode(true));
        });

        subContent.dataset.hydrated = 'true';

        initializeVisibleScrollers(subContent);
      });

      hydrationPromises.set(subContent, promise);

      return promise;
    }

    function showAllProductsOnMobile(mainContent) {
      if (!mainContent) return;

      mainContent.querySelectorAll('.sub-content').forEach(function (content) {
        content.classList.remove('active');
      });

      mainContent.querySelectorAll('.sub-tab-btn').forEach(function (button) {
        button.classList.remove('active');
      });

      var allContent = mainContent.querySelector('.sub-content--all');
      var allButton = mainContent.querySelector('.sub-tab-btn[data-sub^="all-"]');

      if (allContent) {
        allContent.classList.add('active');
      }

      if (allButton) {
        allButton.classList.add('active');
      }
    }

    function handleMainTabClick(event) {
      var tabButton = event.target.closest('.main-tab-btn');

      if (!tabButton || !sectionElement.contains(tabButton)) return;

      var collectionId = tabButton.getAttribute('data-collection');
      var targetCollection = collectionId ? sectionElement.querySelector('#' + CSS.escape(collectionId)) : null;

      // Put the panel's markup in the DOM before it becomes .active so the
      // fade-in animates real content.
      materializeTabPanel(targetCollection);

      sectionElement.querySelectorAll('.main-tab-btn').forEach(function (button) {
        var isActive = button === tabButton;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      sectionElement.querySelectorAll('.collection-tab-content').forEach(function (content) {
        content.classList.toggle('active', content === targetCollection);
      });

      setViewAllUrl(tabButton);
      scrollTabIntoView(tabButton, true);

      if (!targetCollection) return;

      if (!isDesktop()) {
        showAllProductsOnMobile(targetCollection);
      }

      resetVisibleScrollerPositions(targetCollection);
      initializeVisibleScrollers(targetCollection);
    }

    function getSubContentForButton(subTabButton) {
      var parentMainContent = subTabButton.closest('.collection-tab-content');
      var subId = subTabButton.getAttribute('data-sub');

      if (!parentMainContent || !subId) return null;

      return parentMainContent.querySelector('#' + CSS.escape(subId));
    }

    function handleDesktopSubTabClick(event) {
      var subTabButton = event.target.closest('.sub-tab-btn');

      if (!subTabButton || !sectionElement.contains(subTabButton) || !isDesktop()) {
        return;
      }

      var parentMainContent = subTabButton.closest('.collection-tab-content');
      var targetSubContent = getSubContentForButton(subTabButton);

      if (!parentMainContent || !targetSubContent) return;

      // Instant feedback that the click registered, independent of how
      // long the data takes to arrive.
      parentMainContent.querySelectorAll('.sub-tab-btn').forEach(function (button) {
        button.classList.toggle('active', button === subTabButton);
      });

      // The actual tab swap (and whatever CSS reveal animation is tied to
      // .active) waits for real content, so it never animates an empty
      // container. On an already-hydrated/prefetched tab this promise is
      // already resolved, so this next tick is imperceptible.
      hydrateSubContent(targetSubContent).then(function () {
        parentMainContent.querySelectorAll('.sub-content').forEach(function (content) {
          content.classList.toggle('active', content === targetSubContent);
        });

        resetVisibleScrollerPositions(targetSubContent);
        initializeVisibleScrollers(targetSubContent);
      });
    }

    function handleDesktopSubTabHover(event) {
      var subTabButton = event.target.closest('.sub-tab-btn');

      if (!subTabButton || !sectionElement.contains(subTabButton) || !isDesktop()) {
        return;
      }

      var targetSubContent = getSubContentForButton(subTabButton);

      if (!targetSubContent || targetSubContent.dataset.hydrated === 'true') return;

      var handle = targetSubContent.getAttribute('data-collection-handle');

      if (!handle) return;

      // Prefetch only - warms fetchSubCollectionProducts' cache so the
      // click handler's hydrateSubContent() usually finds the data
      // already there instead of starting the fetch from scratch.
      fetchSubCollectionProducts(handle);
    }

    function bindDesktopSubcollectionScript() {
      if (!isDesktop() || desktopSubTabsBound) return;

      sectionElement.addEventListener('click', handleDesktopSubTabClick);
      sectionElement.addEventListener('mouseover', handleDesktopSubTabHover);
      desktopSubTabsBound = true;
    }

    function unbindDesktopSubcollectionScript() {
      if (!desktopSubTabsBound) return;

      sectionElement.removeEventListener('click', handleDesktopSubTabClick);
      sectionElement.removeEventListener('mouseover', handleDesktopSubTabHover);
      desktopSubTabsBound = false;
    }

    function handleViewportMode() {
      if (isDesktop()) {
        bindDesktopSubcollectionScript();
        initializeVisibleScrollers(sectionElement);

        var activeMainContent = sectionElement.querySelector('.collection-tab-content.active');
        scheduleSidebarHeightSync(activeMainContent);
      } else {
        unbindDesktopSubcollectionScript();

        sectionElement.querySelectorAll('.sub-tab-sidebar-wrapper').forEach(function (sidebarWrapper) {
          sidebarWrapper.style.removeProperty('--sub-tab-sidebar-height');
          sidebarWrapper.removeAttribute('data-measured-sidebar-height');
        });

        var activeMainContentMobile = sectionElement.querySelector('.collection-tab-content.active');
        showAllProductsOnMobile(activeMainContentMobile);
      }

      scrollTabIntoView(getActiveMainTab(), false);
    }

    sectionElement.addEventListener('click', handleMainTabClick);

    window.addEventListener('resize', function () {
      if (resizeFrame) {
        window.cancelAnimationFrame(resizeFrame);
      }

      resizeFrame = window.requestAnimationFrame(function () {
        handleViewportMode();
      });
    });

    window.addEventListener('load', function () {
      handleViewportMode();
    });

    setViewAllUrl(getActiveMainTab());
    scrollTabIntoView(getActiveMainTab(), false);
    handleViewportMode();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        handleViewportMode();
      });
    }

    window.setTimeout(handleViewportMode, 100);
  }

  function initializeAll(root) {
    var sections = (root || document).querySelectorAll('[data-ac-collection-tabs]');

    Array.prototype.forEach.call(sections, initializeCollectionTabs);
  }

  window.acCollectionTabsInit = initializeCollectionTabs;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeAll();
    });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) {
      initializeAll(event.target);
    }
  });
})();
